import Combos = require("VSS/Controls/Combos");
import Controls = require("VSS/Controls");
import Diag = require("VSS/Diag");
import Dialogs = require("VSS/Controls/Dialogs");
import Notifications = require("VSS/Controls/Notifications");
import Utils_String = require("VSS/Utils/String");
import WITConstants = require("Presentation/Scripts/TFS/Generated/TFS.WorkItemTracking.Constants");
import WITOM = require("WorkItemTracking/Scripts/TFS.WorkItemTracking");
import WorkItemTrackingResources = require("WorkItemTracking/Scripts/Resources/TFS.Resources.WorkItemTracking");

import { ProjectVisibility } from "TFS/Core/Contracts";
import { publishErrorToTelemetry } from "VSS/Error";
import { delegate } from "VSS/Utils/Core";
import { equals } from "VSS/Utils/String";
import { globalProgressIndicator } from "VSS/VSS";

export interface CreateCopyOfWorkItemDialogResult {
    project: string;
    type: string;
    copyLinks: boolean;
    copyAttachments: boolean;
    copyChildren: boolean;
    copiedWorkItem?: WITOM.WorkItem;
}

export interface CreateCopyOfWorkItemDialogOptions extends Dialogs.IModalDialogOptions {
    workItem?: WITOM.WorkItem;
}

export class CreateCopyOfWorkItemDialog extends Dialogs.ModalDialogO<CreateCopyOfWorkItemDialogOptions> {
    public static enhancementTypeName: string = "CreateCopyDialog";

    private _projectDropdown: Combos.Combo;
    private _workItemTypeDropdown: Combos.Combo;
    private _$copyLinksCheckbox: JQuery;
    private _$copyAttachmentsCheckbox: JQuery;
    private _$copyChildrenCheckbox: JQuery;
    private _projectVisibilityMap: IDictionaryStringTo<ProjectVisibility>; // key: project name, value: ProjectVisibility
    private _messageArea: Notifications.MessageAreaControl;
    private _sourceProjectIndex: number;
    private _sourceProjectName: string;
    private _copiedWorkItem: WITOM.WorkItem;

    constructor(options?) {
        super({
            ...options,
            cssClass: "create-copy-of-work-item-dialog",
            minHeight: 300
        });

        Diag.Debug.assert(options.workItem instanceof WITOM.WorkItem, "Need a WorkItem object to copy.");

        const workItem: WITOM.WorkItem = options.workItem;
        this._projectVisibilityMap = {};
        this._sourceProjectName = workItem.getOriginalWorkItemType().project.name;
    }

    public initialize() {
        super.initialize();
        this.setTitle(WorkItemTrackingResources.CreateCopyOfWorkItemTitle);

        this._decorate();
        this._populateProjects();
    }

    public getDialogResult(): CreateCopyOfWorkItemDialogResult {
        return {
            project: this._projectDropdown.getText(),
            type: this._workItemTypeDropdown.getText(),
            copyLinks: this._copyLinks(),
            copyAttachments: this._copyAttachments(),
            copyChildren: this._copyChildren(),
            copiedWorkItem: this._copiedWorkItem
        };
    }

    public async onOkClick(e?: JQueryEventObject) {
        if (this._copyChildren() && this._options.workItem) {
            // in the normal case, we only create the copy client side and then present an editor for the user to save the item to the server
            // but if we're copying children, we have to do the copy immediately so that we can create and save the child items to the server
            // we do the copy here in the dialog so that we can present any errors that may happen
            let indicatorId = 0;
            try {
                this.showBusyOverlay();
                indicatorId = globalProgressIndicator.actionStarted("copyWorkItem", true);
                const workItem = this._options.workItem;
                const copiedWorkItem = await workItem.copyWithChildren(workItem.workItemType, this._copyLinks(), this._copyAttachments());

                const title = copiedWorkItem.getTitle() + WorkItemTrackingResources.CreateCopyOfWorkItem_CopiedWorkItemSuffix;
                copiedWorkItem.setFieldValue(WITConstants.CoreField.Title, title);

                await copiedWorkItem.saveAsync();
                this._copiedWorkItem = copiedWorkItem;
                super.onOkClick(e);
            } catch (error) {
                const errorText = error.message || String(error);
                console.error(error);
                this._messageArea.setError(`${WorkItemTrackingResources.CreateCopyOfWorkItem_Failed}${errorText}`);
                publishErrorToTelemetry(error);
            } finally {
                this.hideBusyOverlay();
                if (indicatorId > 0) {
                    globalProgressIndicator.actionCompleted(indicatorId);
                }
            }
        } else {
            super.onOkClick(e);
        }
    }

    public onClose(e?) {
        this._options.workItem = null;
        super.onClose(e);
    }

    private _decorate() {
        const $element = this._element;
        const workItem = this._options.workItem;
        const id = Controls.getId();

        const $messageAreaContainer = $("<div/>").addClass("form-section");
        this._messageArea = Controls.Control.create(Notifications.MessageAreaControl, $messageAreaContainer, { showIcon: true });
        $element.append($messageAreaContainer);

        // Adding source work item description
        $element.append(
            $("<p />")
                .addClass("create-copy-title")
                .text(
                    Utils_String.format(
                        WorkItemTrackingResources.CreateCopyOfWorkItemSourceFormat,
                        workItem.workItemType.name,
                        workItem.id,
                        workItem.getTitle()
                    )
                )
        );

        // Creating project drop down
        $element.append(
            "<p><label for='project'>" +
                WorkItemTrackingResources.CreateCopyOfWorkItemProject +
                "</label><input id='project' name='project' type='text' /></p>"
        );
        this._projectDropdown = <Combos.Combo>(
            Controls.Enhancement.enhance(Combos.Combo, $element.find("input[name='project']"), {
                allowEdit: false,
                indexChanged: delegate(this, this._onProjectChange)
            })
        );

        // Creating work item type drop down
        $element.append(
            "<p><label for='wit'>" + WorkItemTrackingResources.CreateCopyOfWorkItemType + "</label><input id='wit' name='wit' type='text' /></p>"
        );
        this._workItemTypeDropdown = <Combos.Combo>(
            Controls.Enhancement.enhance(Combos.Combo, $element.find("input[name='wit']"), {
                allowEdit: false,
                indexChanged: this._onWorkItemTypeChange
            })
        );

        const copyLinksCheckboxId = `createCopyOfWorkItem_copyLinksCheckbox_${id}`;
        const $copyLinksInputContainer = $("<div/>").addClass("include-input-container").appendTo($element);
        this._$copyLinksCheckbox = $("<input/>").attr("type", "checkbox").attr("id", copyLinksCheckboxId).appendTo($copyLinksInputContainer);
        $("<label />")
            .attr("for", copyLinksCheckboxId)
            .text(WorkItemTrackingResources.CreateCopyOfWorkItem_IncludeLinks)
            .appendTo($copyLinksInputContainer);

        const copyAttachmentsCheckboxId = `createCopyOfWorkItem_copyAttachmentssCheckbox_${id}`;
        const $copyAttachmentsInputContainer = $("<div/>").addClass("include-input-container").appendTo($element);
        this._$copyAttachmentsCheckbox = $("<input name='include' />")
            .attr("type", "checkbox")
            .attr("id", copyAttachmentsCheckboxId)
            .appendTo($copyAttachmentsInputContainer);
        $("<label />")
            .attr("for", copyAttachmentsCheckboxId)
            .text(WorkItemTrackingResources.CreateCopyOfWorkItem_IncludeAttachments)
            .appendTo($copyAttachmentsInputContainer);

        const copyChildrenCheckboxId = `createCopyOfWorkItem_copyChildrensCheckbox_${id}`;
        const $copyChildrenInputContainer = $("<div/>").addClass("include-input-container").appendTo($element);
        this._$copyChildrenCheckbox = $("<input name='include' />")
            .attr("type", "checkbox")
            .attr("id", copyChildrenCheckboxId)
            .appendTo($copyChildrenInputContainer);
        $("<label />")
            .attr("for", copyChildrenCheckboxId)
            .text(WorkItemTrackingResources.CreateCopyOfWorkItem_IncludeChildren)
            .appendTo($copyChildrenInputContainer);
    }

    private _copyLinks(): boolean {
        if (this._$copyLinksCheckbox) {
            return this._$copyLinksCheckbox.prop("checked");
        }
        return false;
    }

    private _copyAttachments(): boolean {
        if (this._$copyAttachmentsCheckbox) {
            return this._$copyAttachmentsCheckbox.prop("checked");
        }
        return false;
    }

    private _copyChildren(): boolean {
        return !!this._$copyChildrenCheckbox?.prop("checked");
    }

    private _populateProjects() {
        let i;
        let len;
        const workItem = this._options.workItem;
        const projectPicker = this._projectDropdown;

        // Loading projects first
        workItem.store.beginGetProjects((projects: WITOM.Project[]) => {
            let p,
                selectedProject,
                projectSource = [];

            // Sorting projects according to the name aplhabetically
            projects.sort(function (p1, p2) {
                return p1.name.localeCompare(p2.name, navigator.language);
            });

            // Iterating through projects to populate source of combo
            for (i = 0, len = projects.length; i < len; i++) {
                p = projects[i].name;
                projectSource[projectSource.length] = p;

                // Trying to identify the selected project for the combo
                if (p === workItem.project.name) {
                    selectedProject = projects[i];
                }
            }

            // Ensuring first project to be selected if nothing found
            selectedProject = selectedProject || projects[0];

            // Setting source of project combo
            projectPicker.setSource(projectSource);

            // Setting selected project
            projectPicker.setText(selectedProject.name);

            // build projects map
            projects.forEach((p) => (this._projectVisibilityMap[p.name] = p.visibility));

            this._sourceProjectIndex = projectSource.indexOf(this._sourceProjectName);

            selectedProject.beginGetVisibleWorkItemTypeNames((witNames) => {
                this._populateWits(witNames);
            });

            Diag.logTracePoint("CreateCopyOfWorkItemDialog._populateProjects.complete");
        });
    }

    private _populateWits(witNames) {
        let i,
            len,
            wit,
            selectedWit,
            workItem = this._options.workItem,
            witSource = [];

        // Loading work item type names of the selected project
        for (i = 0, len = witNames.length; i < len; i++) {
            wit = witNames[i];
            witSource[witSource.length] = wit;

            if (wit === workItem.workItemType.name) {
                selectedWit = wit;
            }
        }

        selectedWit = selectedWit || witNames[0];

        // Setting source of work item type combo
        this._workItemTypeDropdown.setSource(witSource);

        // Setting initial value for work item type combo
        this._workItemTypeDropdown.setText(selectedWit);

        // Enabling OK button because we have enough input right now to create the copy of work item
        this.updateOkButton(true);
        this._setCopyChildrenEnabled();
    }

    private _onProjectChange(index) {
        const projectName = this._projectDropdown.getText();
        const project: WITOM.Project = this._options.workItem.store.projects.find((p) => equals(p.name, projectName, true));

        if (this._isNewProject() && this._isPublicProject(projectName)) {
            this._showPublicProjectVisibilityMessage();
        } else {
            this._messageArea.clear();
        }

        project.beginGetVisibleWorkItemTypeNames((witNames) => {
            this._populateWits(witNames);
        });

        this._setCopyChildrenEnabled();
    }

    private _onWorkItemTypeChange = (index: number) => {
        this._setCopyChildrenEnabled();
    };

    private _setCopyChildrenEnabled = () => {
        const shouldBeEnabled = this._options.workItem.workItemType.name === this._workItemTypeDropdown.getText() && !this._isNewProject();
        const isEnabled = !this._$copyChildrenCheckbox.attr("disabled");
        if (isEnabled !== shouldBeEnabled) {
            if (shouldBeEnabled) {
                this._$copyChildrenCheckbox.prop("disabled", false);
            } else {
                this._$copyChildrenCheckbox.attr("disabled", "disabled");
                this._$copyChildrenCheckbox.prop("checked", false);
            }
        }
    };

    /**
     * true if the currently selected project isn't the project that the work item belongs to
     */
    private _isNewProject(): boolean {
        return this._projectDropdown.getSelectedIndex() !== this._sourceProjectIndex;
    }

    private _isPublicProject(projectName: string): boolean {
        return this._projectVisibilityMap.hasOwnProperty(projectName) && this._projectVisibilityMap[projectName] === ProjectVisibility.Public;
    }

    private _showPublicProjectVisibilityMessage() {
        const $message = $("<span />")
            .text(WorkItemTrackingResources.CopyWorkItemToPublicProjectMessage)
            .append(
                $("<a />")
                    .attr({
                        href: WorkItemTrackingResources.WorkItemPublicVisibilityLearnMoreLink,
                        target: "_blank",
                        rel: "noopener noreferrer"
                    })
                    .text(WorkItemTrackingResources.WorkItemMoveCopyPublicVisibilityLearnMoreLinkText)
            );

        this._messageArea.setMessage($message, Notifications.MessageAreaType.Info);
    }
}
